import { getTextNodesInElement } from "lib/text/char-position";
import {
  findXpointerAnchor,
  resolveXpointerRange,
} from "lib/text/xpointer-range";

import type { HighlightTarget } from "./use-highlights";

type QuoteHitTarget = { el: HTMLElement; x: number; y: number };

/**
 * Фолбек для старых браузеров, не поддерживающих CSS highlights.
 */

let marks = new Map<string, HTMLElement[]>();

function createMark(quoteId: string, color: string): HTMLElement {
  const mark = document.createElement("mark");
  mark.className = "highlight";
  mark.dataset.quoteId = quoteId;
  mark.classList.add(`quote-${color}`);
  return mark;
}

function unwrapMark(mark: HTMLElement) {
  const parent = mark.parentNode;
  if (!parent) return;
  while (mark.firstChild) {
    parent.insertBefore(mark.firstChild, mark);
  }
  parent.removeChild(mark);
}

function wrapRange(
  startNode: Node,
  startOffset: number,
  endNode: Node,
  endOffset: number,
  quoteId: string,
  color: string
): HTMLElement | null {
  try {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    const mark = createMark(quoteId, color);
    range.surroundContents(mark);
    return mark;
  } catch {
    return null;
  }
}

function getElementsBetween(startEl: Element, endEl: Element): HTMLElement[] {
  if (startEl.parentElement !== endEl.parentElement) return [];
  const elements: HTMLElement[] = [];
  let current = startEl.nextElementSibling;
  while (current && current !== endEl) {
    if (current instanceof HTMLElement) {
      elements.push(current);
    }
    current = current.nextElementSibling;
  }
  return elements;
}

function highlightRange(target: HighlightTarget): HTMLElement[] {
  const { id, startXpointer, endXpointer, color } = target;
  const range = resolveXpointerRange(startXpointer, endXpointer);
  if (!range) return [];

  const startEl = findXpointerAnchor(range.startContainer);
  const endEl = findXpointerAnchor(range.endContainer);
  if (!startEl || !endEl) return [];

  if (startEl === endEl) {
    const mark = createMark(id, color);
    try {
      range.surroundContents(mark);
      return [mark];
    } catch {
      return [];
    }
  }

  const result: HTMLElement[] = [];

  const startTextNodes = getTextNodesInElement(startEl);
  const lastStartNode = startTextNodes[startTextNodes.length - 1];
  if (lastStartNode) {
    const mark = wrapRange(
      range.startContainer,
      range.startOffset,
      lastStartNode,
      lastStartNode.length,
      id,
      color
    );
    if (mark) result.push(mark);
  }

  for (const middleEl of getElementsBetween(startEl, endEl)) {
    const mark = wrapRange(
      middleEl,
      0,
      middleEl,
      middleEl.childNodes.length,
      id,
      color
    );
    if (mark) result.push(mark);
  }

  const endTextNodes = getTextNodesInElement(endEl);
  const firstEndNode = endTextNodes[0];
  if (firstEndNode) {
    const mark = wrapRange(
      firstEndNode,
      0,
      range.endContainer,
      range.endOffset,
      id,
      color
    );
    if (mark) result.push(mark);
  }

  return result;
}

export function renderHighlights(targets: HighlightTarget[]): {
  clear(): void;
} {
  for (const target of targets) {
    const existing = document.querySelectorAll(
      `mark.highlight[data-quote-id="${target.id}"]`
    );
    if (existing.length > 0) continue;

    const targetMarks = highlightRange(target);
    if (targetMarks.length > 0) {
      marks.set(target.id, targetMarks);
    }
  }

  return {
    clear() {
      marks.forEach(m => m.forEach(unwrapMark));
      marks = new Map();
    },
  };
}

export function getQuoteIdAt({ el }: QuoteHitTarget): string | null {
  const mark = el.closest("mark[data-quote-id]") as HTMLElement | null;
  return mark?.dataset?.quoteId ?? null;
}

export function getQuoteElement(quoteId: string): Element | null {
  return document.querySelector(`mark[data-quote-id="${quoteId}"]`);
}

const PREVIEW_ID = "__preview__";

export function setPreviewColor(range: Range, color: string) {
  clearPreview();
  const result: HTMLElement[] = [];
  const textNodes = getTextNodesInRange(range);

  for (const { node, start, end } of textNodes) {
    const mark = wrapRange(node, start, node, end, PREVIEW_ID, color);
    if (mark) result.push(mark);
  }

  if (result.length > 0) {
    marks.set(PREVIEW_ID, result);
  }
}

function getTextNodesInRange(
  range: Range
): Array<{ node: Text; start: number; end: number }> {
  const result: Array<{ node: Text; start: number; end: number }> = [];
  const ancestor =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentElement!
      : (range.commonAncestorContainer as Element);

  const walker = document.createTreeWalker(ancestor, NodeFilter.SHOW_TEXT);
  let node: Text | null;
  let inRange = false;

  while ((node = walker.nextNode() as Text | null)) {
    if (node === range.startContainer) {
      inRange = true;
      const end = node === range.endContainer ? range.endOffset : node.length;
      if (range.startOffset < end) {
        result.push({ node, start: range.startOffset, end });
      }
      if (node === range.endContainer) break;
      continue;
    }
    if (node === range.endContainer) {
      if (range.endOffset > 0) {
        result.push({ node, start: 0, end: range.endOffset });
      }
      break;
    }
    if (inRange) {
      result.push({ node, start: 0, end: node.length });
    }
  }

  return result;
}

export function clearPreview() {
  const previewMarks = marks.get(PREVIEW_ID);
  if (previewMarks) {
    previewMarks.forEach(unwrapMark);
    marks.delete(PREVIEW_ID);
  }
}

export function setQuoteColor(quoteId: string, color: string) {
  const elements = document.querySelectorAll(
    `mark[data-quote-id="${quoteId}"]`
  );
  for (const el of elements) {
    for (const cls of el.classList) {
      if (cls.startsWith("quote-")) el.classList.remove(cls);
    }
    el.classList.add(`quote-${color}`);
  }
}
